/**
 * R1: Same-seed exact duplicate merge [AUTO-APPLY, confidence=1.0]
 *
 * Trigger: nody w TYM SAMYM seedzie z identycznym (primary_entity_raw, primary_intent).
 * Safety: jeśli którykolwiek dup ma matched_wp_post_id → SKIP całą grupę.
 * Action: wybór canonical (SV desc → incoming desc → slug len asc → slug asc) →
 *   rewire relations z deleted na canonical → delete dupes → cleanup self-loops.
 *
 * Pure function — nie dotyka DB. Orchestrator bierze operations[] i robi atomic write.
 */

import type {
  RepairRuleInput,
  RepairRuleResult,
  MergeOp,
  SkippedOp,
} from "@/types/mapRepair";
import { pickCanonical, groupBySeedEntityIntent } from "./_shared";

export function runR1(input: RepairRuleInput): RepairRuleResult {
  const operations: RepairRuleResult["operations"] = [];
  const skipped: SkippedOp[] = [];
  const groups = groupBySeedEntityIntent(input.nodes);

  for (const [key, nodesInGroup] of groups.entries()) {
    if (nodesInGroup.length < 2) continue;

    // Safety check — jakikolwiek dup ma matched_wp_post → skip całą grupę
    const hasMatch = nodesInGroup.find((n) => n.matched_wp_post_id);
    if (hasMatch) {
      for (const n of nodesInGroup) {
        skipped.push({
          node_id: n.id,
          slug: n.slug,
          rule: "R1",
          reason: `group_has_wp_post (matched: ${hasMatch.slug})`,
        });
      }
      continue;
    }

    const canonical = pickCanonical(nodesInGroup, input.incomingByNodeId);
    const deleted = nodesInGroup.filter((n) => n.id !== canonical.id);

    const [, entity, intent] = key.split("|");

    // Rewire plan — które relacje idą do canonical
    const deletedIds = new Set(deleted.map((n) => n.id));
    const affectedRelations = input.relations.filter(
      (r) => deletedIds.has(r.from_node_id) || deletedIds.has(r.to_node_id),
    );
    const affectedRelationIds = new Set(affectedRelations.map((r) => r.id));

    // Istniejące relacje które NIE są affected — klucze uq_relation (map_id, from, to, type)
    // Używamy tego żeby uniknąć UNIQUE violation podczas rewire.
    const relKey = (
      mapId: string,
      from: string,
      to: string,
      type: string | null,
    ) => `${mapId}|${from}|${to}|${type ?? ""}`;
    const existingKeys = new Set<string>();
    for (const r of input.relations) {
      if (!affectedRelationIds.has(r.id)) {
        existingKeys.add(relKey(r.map_id, r.from_node_id, r.to_node_id, r.relation_type));
      }
    }

    const rewireOps: Array<{
      relation_id: string;
      old_from: string;
      old_to: string;
      new_from: string;
      new_to: string;
    }> = [];
    const selfLoopRelationIds: string[] = [];
    const redundantDuplicateIds: string[] = [];

    for (const r of affectedRelations) {
      const newFrom = deletedIds.has(r.from_node_id)
        ? canonical.id
        : r.from_node_id;
      const newTo = deletedIds.has(r.to_node_id) ? canonical.id : r.to_node_id;

      // Case 1: self-loop (no_self_relation CHECK constraint)
      if (newFrom === newTo) {
        selfLoopRelationIds.push(r.id);
        continue;
      }

      // Case 2: would create duplicate (uq_relation UNIQUE constraint)
      const newKey = relKey(r.map_id, newFrom, newTo, r.relation_type);
      if (existingKeys.has(newKey)) {
        redundantDuplicateIds.push(r.id);
        continue;
      }

      // Case 3: safe rewire — mark nowy klucz żeby kolejne rewires nie stworzyły drugiego
      existingKeys.add(newKey);
      rewireOps.push({
        relation_id: r.id,
        old_from: r.from_node_id,
        old_to: r.to_node_id,
        new_from: newFrom,
        new_to: newTo,
      });
    }

    const mergeOp: MergeOp = {
      canonical: {
        id: canonical.id,
        slug: canonical.slug,
        seed_id: canonical.seed_id,
      },
      deleted: deleted.map((n) => ({
        id: n.id,
        slug: n.slug,
        seed_id: n.seed_id,
      })),
      entity,
      intent,
      relations_rewired: rewireOps.length,
      self_loops_removed: selfLoopRelationIds.length,
    };

    operations.push({
      operation: "merge",
      payload: {
        rule: "R1",
        merge: mergeOp,
        rewire: rewireOps,
        self_loops: selfLoopRelationIds,
        redundant_duplicates: redundantDuplicateIds,
      },
      rollback_payload: {
        deleted_nodes: deleted.map((n) => ({ ...n })),
        rewired_relations: affectedRelations.map((r) => ({
          id: r.id,
          from_node_id: r.from_node_id,
          to_node_id: r.to_node_id,
          relation_type: r.relation_type,
          weight: r.weight,
          map_id: r.map_id,
        })),
      },
    });
  }

  return {
    rule: "R1",
    operations,
    proposals: [],
    flags: [],
    skipped,
  };
}
